/**
 * Format a review source name for display.
 * - Empty/undefined source defaults to "Google"
 * - "bbb" becomes "BBB"
 * - "dealerrater" becomes "DealerRater"
 * - Other sources are capitalized (first letter uppercase)
 */
export function formatSourceName(source: string | undefined | null): string {
  if (!source) {
    return 'Google';
  }
  
  const sourceLower = source.toLowerCase();
  
  // Special case mappings
  const sourceMap: Record<string, string> = {
    'bbb': 'BBB',
    'dealerrater': 'DealerRater',
    'opentable': 'OpenTable',
    'tripadvisor': 'TripAdvisor',
    'carscom': 'Cars.com',
    'carfax': 'CarFax',
    'edmunds': 'Edmunds',
    'cargurus': 'CarGurus',
    'booking': 'Booking.com',
    'expedia': 'Expedia',
    'golfpass': 'GolfPass',
  };
  
  if (sourceMap[sourceLower]) {
    return sourceMap[sourceLower];
  }
  
  // Default: capitalize first letter
  return source.charAt(0).toUpperCase() + source.slice(1);
}

/**
 * Group reviews by source and return counts.
 * Returns an array of { source: string, count: number } sorted by count descending.
 */
export function groupReviewsBySource<T extends { source?: string }>(
  reviews: T[]
): { source: string; count: number }[] {
  const counts = new Map<string, number>();
  
  for (const review of reviews) {
    const source = formatSourceName(review.source);
    counts.set(source, (counts.get(source) ?? 0) + 1);
  }
  
  return Array.from(counts.entries())
    .map(([source, count]) => ({ source, count }))
    .sort((a, b) => b.count - a.count);
}
